<template>
  <div class="dependents-container">
    <transition name="view" mode="out-in">
      <c-loader v-if="loading" :size="isMobile ? 79 : 99" />

      <form-group
        v-else
        ref="formGroup"
        :label-left="!isMobile"
        :schemas="schemas"
        :data="data"
        :disabled="submitting"
        :disabled-forms="disabledForms"
        :required-fields="requiredFields"
        @add:dependent="onAddDependent"
        @remove:dependent="onRemoveDependent"
        @file:add="onFileModalOpen"
        @file:remove="onRemoveFile"
      />
    </transition>

    <c-form-builder-modal
      v-if="showFileModal"
      label-left
      submit-button-text="Salvar"
      name="dependent"
      title="Anexar Arquivos"
      v-model="filesFormData"
      :required-fields="requiredFields"
      :is-loading="submitting"
      :is-disabled="submitting"
      :schema="filesSchema"
      @close="onFileModalClose"
      @submit="onFileModalSubmit"
    />

    <dialog-modal
      v-if="removing"
      theme="error"
      title="Deseja excluir esse Dependente?"
      message="As informações inseridas serão excluídas do formulário.
      Tem certeza que deseja excluir este registro?"
      @cancel="onRemoveDependentCancel"
      @confirm="onRemoveDependentSubmit"
    />

    <dialog-modal
      v-if="showRemoveFileModal"
      theme="error"
      title="Deseja prosseguir?"
      message="Ao prosseguir os arquivos apagados serão permanentemente excluídos,
        esta ação não pode ser desfeita."
      confirm="Excluir"
      cancel="Não, mudei de ideia"
      @cancel="showRemoveFileModal = false"
      @confirm="onFileModalSubmit(filesFormData)"
    />

    <dialog-modal
      v-if="showEmptyNameAlert"
      theme="primary"
      title="Nome do dependente obrigatório"
      message="Antes de inserir um documento, é necessário especificar o nome do dependente."
      confirm="Confirmar"
      character="warning"
      :show-cancel-button="false"
      @confirm="showEmptyNameAlert = false"
    />

    <dialog-modal
      v-if="isRemovingFile"
      theme="error"
      title="Deseja excluir o arquivo selecionado?"
      message="O arquivo será permanentemente excluído, esta ação não pode ser desfeita."
      confirm="Excluir"
      cancel="Não, mudei de ideia"
      :loading="submitting"
      @cancel="isRemovingFile = false"
      @confirm="onRemoveFileSubmit"
    />
  </div>
</template>

<script>
import { computed, getCurrentInstance } from 'vue'
import { mapState, mapMutations, mapActions } from 'vuex'
import * as types from '@types'

import { MediaQuery } from '@convenia/mixins'
import FormGroup from '@components/FormGroup'
import DialogModal from '@components/DialogModal'
import dependentSchema from '@convenia/employee-organisms/SystemFields/Dependents/content/forms/dependents'
import useSystemFields from '@convenia/employee-organisms/SystemFields/composables/useSystemFields'

export default {
  name: 'DependentsStepper',

  mixins: [ MediaQuery ],

  components: {
    FormGroup,
    DialogModal
  },

  setup () {
    const { proxy } = getCurrentInstance()

    const {
      formSchema,
      getFormValues,
      requiredFields,
    } = useSystemFields({
      schema: computed(() => dependentSchema(proxy)),
      systemFields: computed(() => proxy.systemFields.dependents || {}),
      customFields: computed(() => proxy.customFields.dependents || {}),
    })

    return {
      formSchema,
      getFormValues,
      requiredFields,
    }
  },

  computed: {
    ...mapState({
      dependents: state => state.dependent.dependents,
      systemFields: state => state.dependent.systemFields,
      customFields: state => state.dependent.customFields,
      employeeId: state => state.admission.employeeUuid,
    }),

    data () {
      return {
        dependent: this.dependents.map(({ customFieldValues, ...dependent }) => (
          this.getFormValues(dependent, customFieldValues)
        ))
      }
    },

    hasAttachmentField () {
      return !!this.formSchema?.documentFiles
    },

    schemas () {
      const { documentFiles, ir, familySalary, foreigner, ...fields } = this.formSchema || {}

      return {
        dependent: {
          title: 'Dependentes',
          singleTitle: 'Dependente',
          singleIcon: 'user-profile-checkmark',
          addLabel: 'Adicionar novo dependente',
          files: this.hasAttachmentField,
          filesText: 'Documentos relacionados ao dependente.',
          icon: 'group-user',
          multiple: true,
          required: false,
          fields: {
            ...fields,
            ...(ir ? { ir } : {}),
            ...(familySalary ? { familySalary } : {}),
            ...(foreigner ? { foreigner } : {}),
            documentFiles: { ...documentFiles, hide: true }
          }
        }
      }
    },

    filesSchema () {
      const { documentFiles } = this.formSchema || {}

      return { documentFiles }
    },

    selected () {
      if (!this.active) return {}

      return this.dependents.find(({ id }) => id === this.active)
    },

    submitting: {
      get () { return this.$store.state.admission.submitting },
      set (value) { this.$store.commit(types.ADMISSION_SUBMITTING, value) }
    },

    disabledForms () {
      return this.$store.getters.hasDependent ? [] : [ 'dependent' ]
    }
  },

  data: () => ({
    loading: true,
    removing: false,
    active: null,
    showFileModal: false,
    showRemoveFileModal: false,
    showEmptyNameAlert: false,
    isRemovingFile: false,
    removingFile: null,
    filesFormData: {},
  }),

  mounted () {
    this.$root.$on('next', this.onSubmit)
    this.$root.$on('previous', this.onSave)
    this.init()
  },

  beforeDestroy () {
    this.$root.$off('next', this.onSubmit)
    this.$root.$off('previous', this.onSave)
  },

  methods: {
    ...mapMutations({
      addDependent: types.EMPLOYEE_ADD_DEPENDENT,
      saveDependents: types.EMPLOYEE_SET_DEPENDENTS,
      setDependentFiles: types.EMPLOYEE_SET_DEPENDENT_FILES,
    }),

    ...mapActions({
      getSystemFields: types.EMPLOYEE_DEPENDENT_SYSTEM_FIELDS,
      getDependents: types.EMPLOYEE_DEPENDENTS,
      setDependent: types.EMPLOYEE_SET_DEPENDENT,
      setDependents: types.EMPLOYEE_SET_DEPENDENTS,
      deleteDependent: types.EMPLOYEE_DELETE_DEPENDENT,
      deleteFile: types.ADMISSION_DELETE_FILE,
      uploadFiles: types.EMPLOYEE_DEPENDENT_UPLOAD_FILES,
    }),

    async init () {
      await this.getDependents()
      await this.getSystemFields()

      this.loading = false
    },

    onAddDependent () {
      this.addDependent()
      this.$refs.formGroup.formsData.dependent.push(
        this.data.dependent[this.data.dependent.length - 1]
      )
    },

    onRemoveDependent (id) {
      this.active = id

      const data = this.$refs.formGroup.formsData
      const dependent = data.dependent.find(({ id }) => id === this.active)
      const hasContent = Object
        .entries(dependent)
        .filter(([ key ]) => key !== 'id')
        .some(([ , value ]) => !!value)

      if (!hasContent)
        return this.onRemoveDependentSubmit()

      this.removing = true
    },

    onRemoveDependentCancel () {
      this.active = null
      this.removing = false
    },

    async onRemoveDependentSubmit () {
      this.submitting = true
      await this.deleteDependent(this.selected)
      this.submitting = false

      this.$refs.formGroup.removeFormDataItem('dependent', this.active)

      this.active = null
      this.removing = false
    },

    async validate () {
      if (!this.$refs.formGroup)
        return false

      if (!await this.$refs.formGroup.validate())
        return false

      return true
    },

    async onSubmit () {
      if (!await this.validate()) return

      const data = this.$refs.formGroup.formsData
      const hasDependents = this.$refs.formGroup.isFormActive('dependent')

      this.submitting = true

      if (hasDependents) {
        // If employee has dependents update them
        const [ err ] = await this.setDependents(data.dependent)

        this.submitting = false

        if (err?.length)
          return this.$feedback.add({
            type: 'error',
            message: 'Erro',
            highlighted: 'ao tentar salvar Dependentes. Tente novamente.'
          })
      } else {
        // Otherwise delete all existing dependents
        this.loading = true
        await Promise.allSettled((this.dependents || []).map(this.deleteDependent))
        this.loading = false
      }

      this.$feedback.add({
        type: 'success',
        message: 'Dependentes',
        highlighted: 'atualizados com sucesso'
      })

      this.submitting = false
      this.$emit('next')
    },

    onSave () {
      if (!this.$refs.formGroup) return
      const data = this.$refs.formGroup.formsData

      if (data.dependent.length)
        this.saveDependents(data.dependent)

      this.$emit('previous')
    },

    async deleteFiles (files) {
      const { employeeId } = this

      await Promise.all(files.map(
        file => this.deleteFile({ file, employeeId }))
      )
    },

    onFileModalOpen (index) {
      const data = this.$refs.formGroup?.formsData.dependent[index]

      if (!data) return

      if (!data.fullName) {
        this.showEmptyNameAlert = true
        return
      }

      this.active = index

      this.filesFormData.documentFiles = [ ...data.documentFiles || [] ]
      this.showFileModal = true
    },

    onFileModalClose () {
      this.filesFormData = {}
      this.showFileModal = false
      this.active = null
    },

    onRemoveFile ({ file, index }) {
      this.isRemovingFile = true
      this.active = index
      this.removingFile = file
    },

    async onRemoveFileSubmit () {
      const { employeeId } = this

      this.submitting = true

      const [ err ] = await this.deleteFile({
        employeeId,
        file: this.removingFile
      })

      this.submitting = false

      if (err)
        return this.$feedback.add({
          type: 'error',
          message: 'Arquivo',
          highlighted: 'não foi excluído, tente novamente'
        })

      this.$feedback.add({
        type: 'success',
        message: 'Arquivo',
        highlighted: 'excluído com sucesso'
      })

      const data = this.$refs.formGroup?.formsData.dependent[this.active] || {}
      const fileIndex = data.documentFiles?.findIndex(file => file.id === this.removingFile.id)

      if (fileIndex >= 0)
        data.documentFiles.splice(fileIndex, 1)

      this.isRemovingFile = false
    },

    setFiles (id, files) {
      const data = this.$refs.formGroup?.formsData.dependent[this.active]

      if (data) {
        data.id = id
        data.documentFiles = files
      }

      this.setDependentFiles({ id, files })
    },

    async onFileModalSubmit ({ documentFiles: files }) {
      const data = this.$refs.formGroup?.formsData.dependent[this.active] || {}
      const { id } = this.dependents[this.active] || {}
      const { documentFiles: initialFiles = [] } = data

      const addedFiles = files
        .filter(file => !file.done)
        .filter(file => !initialFiles.includes(file))

      const removedFiles = initialFiles
        .filter(file => !files.includes(file))

      if (removedFiles.length > 0 && !this.showRemoveFileModal) {
        this.filesFormData = { documentFiles: files }
        this.showRemoveFileModal = true

        return
      }

      this.showRemoveFileModal = false
      this.submitting = true

      const [ err, dependent ] = await this.setDependent({ id, ...data })

      if (err) {
        this.submitting = false
        return
      }

      await this.deleteFiles(removedFiles)

      const [ uploadErr, uploadedFiles ] = await this.uploadFiles({
        id: dependent.id,
        files: addedFiles,
      })

      if (uploadErr) {
        this.submitting = false
        return
      }

      const updatedFiles = [ ...initialFiles, ...uploadedFiles ]
        .filter(file => !removedFiles.includes(file))
        .map(file => ({ ...file, progress: 0 }))

      this.setFiles(dependent?.id, updatedFiles)

      this.filesFormData = {}
      this.showFileModal = false
      this.submitting = false
      this.active = null
    },
  }
}
</script>

<style lang="scss">
.dependents-container {
  position: relative;

  @include responsive (tablet, desktop) {
    margin-top: 10px;
    background: white;
    border-radius: 5px;
    box-shadow: 2px 8px 16px 0px rgba(0, 0, 0, 0.06);
  }

  & > .c-form-group .c-form-field {
    &[field-name="ir"] { margin-top: 40px; }
  }

  @include responsive (xs-mobile, mobile) {
    & > .c-form-group {
      & > .form:first-child {
        padding-top: 30px;
        & > .c-title { display: none; }
      }
    }
  }
}
</style>
